import Tracker from '@openreplay/tracker'
import { ReactNode, createContext, useContext, useEffect, useRef } from 'react'
import { useUserContext } from '~/core/context'
import { Toolbox } from '~/plugins/toolbox/shared'

const useInternal = () => {
  const { session } = useUserContext()

  const tracker = useRef<Tracker>(null)

  const isUnsecure = import.meta.env.DEV
  const projectKey = import.meta.env.VITE_OPENREPLAY_PROJECT_KEY
  const userId = session.user?.id

  const isAuthorised =
    Toolbox.Utility.isDefined(projectKey) &&
    Toolbox.Utility.isDefined(userId) &&
    !session.isAdmin()

  useEffect(() => {
    if (!isAuthorised) {
      return
    }

    if (!tracker.current) {
      tracker.current = new Tracker({
        projectKey,
        defaultInputMode: 0,
        __DISABLE_SECURE_MODE: isUnsecure,
      })
    }

    tracker.current
      .start()
      .then(() => {
        tracker.current.setUserID(userId)
        tracker.current.setMetadata('email', session.user.email)
        tracker.current.setMetadata('name', session.user.name)
      })
      .catch(() => {})

    return () => {
      try {
        tracker.current.stop()
      } catch {
        // ignore
      }

      tracker.current = null
    }
  }, [isAuthorised, userId])

  return {}
}

type OpenReplayContext = ReturnType<typeof useInternal>

const OpenReplayContext = createContext<OpenReplayContext>(undefined)

export const OpenReplayProvider = ({ children }: { children: ReactNode }) => {
  const value = useInternal()

  return (
    <OpenReplayContext.Provider value={value}>
      {children}
    </OpenReplayContext.Provider>
  )
}

export const useOpenReplayContext = (): OpenReplayContext => {
  const context = useContext(OpenReplayContext)

  if (context === undefined) {
    throw new Error(
      'useOpenReplayContext must be used within a OpenReplayProvider',
    )
  }

  return context
}
